/**
 * ModuleTOC — intra-module table of contents with scrollspy.
 *
 * Solves the "endless scroll" problem on long lesson pages by giving the reader
 * a persistent map of the module's `## ` sections and a sense of where they are.
 *
 * Two responsive variants share one scrollspy core:
 *   - `variant="sidebar"` → vertical sticky list, for md+ side column.
 *   - `variant="chips"`    → horizontal scrollable chip row, for xs/sm above
 *                            the body.
 *
 * Active-section tracking uses a single IntersectionObserver over all heading
 * elements. We pick the *topmost* heading currently intersecting a band near the
 * top of the viewport — a robust, jank-free pattern that doesn't fight the
 * browser's scrolling. Clicking an entry scrolls to the heading via its id
 * (set by rehype-slug) and updates the URL hash.
 *
 * Graceful: renders nothing when there are 0–1 segments (a TOC of one is noise).
 */

import * as React from "react";
import { useTheme } from "@mui/material/styles";
import { Box, Chip, Stack, Typography, alpha } from "@mui/material";
import ListIcon from "@mui/icons-material/List";
import { useTranslation } from "react-i18next";
import type { ModuleSegment } from "../hooks/useModuleSegments";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

// Offset from the top of the viewport where a heading counts as "active".
// Keeps the active item aligned with what the reader is actually looking at,
// accounting for the sticky page header band.
const SCROLL_OFFSET = 96;

interface ModuleTOCProps {
  segments: ModuleSegment[];
  variant: "sidebar" | "chips";
}

export default function ModuleTOC({ segments, variant }: ModuleTOCProps) {
  const theme = useTheme();
  const { t } = useTranslation("common");
  const [activeAnchor, setActiveAnchor] = React.useState<string | null>(
    segments[0]?.anchor ?? null,
  );

  // ---- Scrollspy ---------------------------------------------------------
  React.useEffect(() => {
    if (segments.length === 0) return;

    const headingEls = segments
      .map((s) => document.getElementById(s.anchor))
      .filter((el): el is HTMLElement => el !== null);

    if (headingEls.length === 0) return;

    // Track which headings are within the active band; on each change, choose
    // the one nearest the top of the document among those above the fold line.
    const visible = new Map<string, boolean>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visible.set(entry.target.id, entry.isIntersecting);
        }

        // Active = the last heading whose top has reached the activation line
        // (~top quarter of the viewport). Using a quarter-height line rather
        // than the sticky-header line (96px) makes a section activate as soon
        // as its heading enters the upper reading area, instead of lagging
        // until the heading tucks under the header. The line MUST match the
        // observer band's bottom edge below, or the callback won't fire when a
        // heading crosses it.
        const activationLine = Math.max(SCROLL_OFFSET, window.innerHeight * 0.25);
        let current: string | null = null;
        for (const el of headingEls) {
          if (el.getBoundingClientRect().top <= activationLine + 1) {
            current = el.id;
          }
        }
        // Before the first heading reaches the line, fall back to the first
        // intersecting heading (handles the very top of the page).
        if (!current) {
          const firstVisible = headingEls.find((el) => visible.get(el.id));
          current = firstVisible?.id ?? headingEls[0].id;
        }
        setActiveAnchor(current);
      },
      {
        // Band from just under the sticky header to the 25% activation line, so
        // a callback fires exactly when a heading crosses that line (top or bottom).
        rootMargin: `-${SCROLL_OFFSET}px 0px -75% 0px`,
        threshold: [0, 1],
      },
    );

    headingEls.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [segments]);

  const handleClick = React.useCallback(
    (anchor: string) => (e: React.MouseEvent) => {
      e.preventDefault();
      const el = document.getElementById(anchor);
      if (!el) return;
      const y =
        el.getBoundingClientRect().top + window.scrollY - SCROLL_OFFSET + 8;
      window.scrollTo({ top: y, behavior: "smooth" });
      setActiveAnchor(anchor);
      // Reflect location without a jump (scroll handled above).
      if (window.history.replaceState) {
        window.history.replaceState(null, "", `#${anchor}`);
      }
    },
    [],
  );

  // A TOC with a single entry is noise — render nothing.
  if (segments.length <= 1) return null;

  // ---- Chips variant (xs/sm) --------------------------------------------
  if (variant === "chips") {
    return (
      <Box
        sx={{
          display: { xs: "block", md: "none" },
          mb: 4,
          mx: { xs: -2, sm: 0 },
          px: { xs: 2, sm: 0 },
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: "auto",
            pb: 1,
            // Hide scrollbar but keep scrollability.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {segments.map((s) => {
            const active = s.anchor === activeAnchor;
            return (
              <Chip
                key={s.anchor}
                label={s.title}
                clickable
                onClick={handleClick(s.anchor) as any}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontWeight: 500,
                  color: active ? theme.palette.common.white : theme.palette.primary.main,
                  bgcolor: active ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, active ? 0 : 0.2)}`,
                  "&:hover": {
                    bgcolor: active
                      ? alpha(theme.palette.primary.main, 0.9)
                      : alpha(theme.palette.primary.main, 0.16),
                  },
                }}
              />
            );
          })}
        </Stack>
      </Box>
    );
  }

  // ---- Sidebar variant (md+) --------------------------------------------
  return (
    <Box
      component="nav"
      aria-label={t("ModuleTOC.onThisPage")}
      sx={{
        display: { xs: "none", md: "block" },
        position: "sticky",
        top: 24,
        maxHeight: "calc(100vh - 48px)",
        overflowY: "auto",
        pl: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, pl: 1.5 }}>
        <ListIcon sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: "text.secondary",
          }}
        >
          {t("ModuleTOC.onThisPage")}
        </Typography>
      </Stack>
      <Stack spacing={0} sx={{ borderLeft: `2px solid`, borderColor: "divider" }}>
        {segments.map((s) => {
          const active = s.anchor === activeAnchor;
          return (
            <Box
              key={s.anchor}
              component="a"
              href={`#${s.anchor}`}
              onClick={handleClick(s.anchor)}
              sx={{
                display: "block",
                pl: 1.5,
                py: 0.75,
                ml: "-2px",
                borderLeft: `2px solid ${active ? theme.palette.primary.main : "transparent"}`,
                color: active ? theme.palette.primary.main : "text.secondary",
                fontWeight: 500,
                fontSize: "0.83rem",
                lineHeight: 1.4,
                textDecoration: "none",
                transition: "color 0.15s, border-color 0.15s",
                "&:hover": {
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              {s.title}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
